/**
 * <s-reviews> — renders written reviews that the reviews app stores on each product
 * (scm_review_importer.reviewsData metafield, whose reviewItems value is base64 JSON).
 *
 * Markup contract (all copy and styling live in Liquid):
 *   script[type="application/json"][data-reviews-source] — one per product; JSON of the
 *     metafield value, with optional data-product-title / data-product-url.
 *   template[data-review-card] — card markup with [data-review-stars], [data-review-text],
 *     [data-review-author], [data-review-meta], [data-review-photo], [data-review-product].
 *   [data-reviews-list]       — where the first `data-limit` cards go.
 *   [data-reviews-more]       — optional wrapper (e.g. <details>) shown only when more remain.
 *   [data-reviews-more-list]  — where the remaining cards go.
 *
 * Attributes: data-limit (initial cards), data-max (total cards), data-per-product
 * (max cards per source), data-locale (date formatting).
 */
class SReviews extends HTMLElement {
  // Reviews imported from another marketplace that quote that marketplace's prices or name
  // describe a different shopping experience, so they are hidden rather than reworded.
  static #otherStorePattern = /(\d+([.,]\d+)?\s?(euros?\b|eur\b|€|usd\b|dollars?\b|reais\b|zł|pln\b|£|pounds?\b)|[€$£]\s?\d|r\$\s?\d|ali\s?express)/i;

  static #mentionsOtherStore(text) {
    return SReviews.#otherStorePattern.test(text);
  }

  connectedCallback() {
    if (this.dataset.rendered) return;
    this.dataset.rendered = 'true';

    const template = this.querySelector('template[data-review-card]');
    const list = this.querySelector('[data-reviews-list]');
    if (!template || !list) return;

    const limit = Number(this.dataset.limit || 6);
    const max = Number(this.dataset.max || 30);
    const perProduct = Number(this.dataset.perProduct || 50);

    let reviews = [];
    for (const source of this.querySelectorAll('script[data-reviews-source]')) {
      const items = this.#readSource(source)
        .filter((review) => review.status === undefined || Number(review.status) === 1)
        .filter((review) => !review.is_spam)
        .filter((review) => !SReviews.#mentionsOtherStore(this.#text(review)))
        .map((review) => ({
          ...review,
          productTitle: source.dataset.productTitle || '',
          productUrl: source.dataset.productUrl || '',
        }));
      const withText = items.filter((review) => this.#text(review));
      const chosen = (withText.length > 0 ? withText : items).sort(this.#compare).slice(0, perProduct);
      reviews = reviews.concat(chosen);
    }

    reviews = reviews.sort(this.#compare).slice(0, max);
    if (reviews.length === 0) {
      this.hidden = true;
      return;
    }

    const moreList = this.querySelector('[data-reviews-more-list]');
    const more = this.querySelector('[data-reviews-more]');
    reviews.forEach((review, index) => {
      const card = this.#renderCard(template, review);
      if (index < limit || !moreList) list.append(card);
      else moreList.append(card);
    });
    if (more) more.hidden = !moreList || moreList.children.length === 0;
    this.hidden = false;
  }

  #readSource(source) {
    try {
      let data = JSON.parse(source.textContent || 'null');
      if (typeof data === 'string') data = JSON.parse(data);
      if (data && data.reviewsData) data = data.reviewsData;
      if (typeof data === 'string') data = JSON.parse(data);
      const encoded = data?.reviewItems;
      if (!encoded) return [];
      const binary = atob(encoded);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      const decoded = JSON.parse(new TextDecoder().decode(bytes));
      return Array.isArray(decoded?.reviews) ? decoded.reviews : [];
    } catch {
      return [];
    }
  }

  #text(review) {
    return String(review.review || '').trim();
  }

  #date(review) {
    const match = String(review.date || '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!match) return null;
    return new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  }

  #compare = (a, b) => {
    const textA = this.#text(a).length > 0 ? 1 : 0;
    const textB = this.#text(b).length > 0 ? 1 : 0;
    if (textA !== textB) return textB - textA;
    const photoA = this.#photo(a) ? 1 : 0;
    const photoB = this.#photo(b) ? 1 : 0;
    if (photoA !== photoB) return photoB - photoA;
    return (this.#date(b)?.getTime() || 0) - (this.#date(a)?.getTime() || 0);
  };

  #photo(review) {
    const photos = Array.isArray(review.photosArray) ? review.photosArray : [];
    const first = photos.find((url) => typeof url === 'string' && /^https:\/\//.test(url));
    return first || null;
  }

  #author(review) {
    const parts = String(review.author || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
  }

  #renderCard(template, review) {
    const fragment = template.content.cloneNode(true);
    const rating = Math.max(0, Math.min(5, Math.round(Number(review.rating) || 0)));

    const stars = fragment.querySelector('[data-review-stars]');
    if (stars) {
      stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
      const label = stars.dataset.label;
      if (label) stars.setAttribute('aria-label', label.replace('__rating__', String(rating)));
    }

    const text = fragment.querySelector('[data-review-text]');
    if (text) {
      const value = this.#text(review);
      if (value) text.textContent = value;
      else text.remove();
    }

    const author = fragment.querySelector('[data-review-author]');
    if (author) author.textContent = this.#author(review);

    const meta = fragment.querySelector('[data-review-meta]');
    if (meta) {
      const bits = [];
      const country = String(review.country || '').trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(country)) bits.push(country === 'UK' ? 'GB' : country);
      const date = this.#date(review);
      if (date) {
        bits.push(new Intl.DateTimeFormat(this.dataset.locale || undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(date));
      }
      meta.textContent = bits.join(' · ');
    }

    const photo = fragment.querySelector('[data-review-photo]');
    if (photo) {
      const url = this.#photo(review);
      if (url) photo.src = url;
      else photo.remove();
    }

    const product = fragment.querySelector('[data-review-product]');
    if (product) {
      if (review.productTitle && review.productUrl) {
        product.textContent = review.productTitle;
        product.href = review.productUrl;
      } else {
        product.remove();
      }
    }

    return fragment;
  }
}

if (!customElements.get('s-reviews')) customElements.define('s-reviews', SReviews);
